import React, {useEffect, useState} from 'react'
import {Box} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import TextAreaNew from 'src/components-new/inputs/TextAreaNew'
import MultiSelectNew from 'src/components-new/inputs/MultiSelectNew'
import ReadOnlyField from 'src/components-new/inputs/ReadOnlyField'

/**
 * New-style port of src/components/elements/DocumentsInfoModal/DocumentsInfoModal.tsx,
 * scoped to its usage inside the patient-detail Documents tab.
 *
 * Nome/Descrizione/Categoria are editable (disabled when `readOnly`); Tipo Documento,
 * Nome file and Dimensione are always-disabled display fields, matching legacy behavior.
 * Unlike the legacy antd-form version, the caller is responsible for closing the dialog
 * once its own `onSave` async work completes (mirrors PatientEventDialogNew's contract).
 */

interface DocumentInfoDialogNewProps {
  open: boolean
  document?: Record<string, any>
  categoryOptions: {value: string; label: string}[]
  readOnly: boolean
  onClose: () => void
  onSave: (patch: {category: string[]; name: string; description: string}) => Promise<void> | void
}

function formatSize(size: number | undefined): string {
  return size ? `${Math.round((size / (1024 ** 2)) * 100) / 100} MB` : '-'
}

export default function DocumentInfoDialogNew({
  open,
  document,
  categoryOptions,
  readOnly,
  onClose,
  onSave,
}: DocumentInfoDialogNewProps): JSX.Element {
  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [category, setCategory] = useState<string[]>([])

  useEffect(() => {
    if (!open) return

    setName(document?.name || document?.fileName || '')
    setDescription(document?.description ?? '')
    setCategory(document?.category ?? [])
  }, [open, document])

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={document?.name || document?.fileName || 'Documento'}
      maxWidth="sm"
      actions={
        <Button variant="primary" disabled={readOnly} onClick={() => onSave({category, name, description})}>
          Salva
        </Button>
      }
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
        <TextFieldNew label="Nome" fullWidth value={name} onChange={setName} disabled={readOnly} />
        <TextAreaNew label="Descrizione" fullWidth rows={4} value={description} onChange={setDescription} disabled={readOnly} />
        <ReadOnlyField label="Tipo Documento" value={document?.type} fullWidth />
        <MultiSelectNew
          label="Categoria"
          required
          fullWidth
          value={category}
          onChange={setCategory}
          options={categoryOptions}
          disabled={readOnly}
        />
        <ReadOnlyField label="Nome file" value={document?.fileName} fullWidth />
        <ReadOnlyField label="Dimensione" value={formatSize(document?.size)} fullWidth />
      </Box>
    </AppDialog>
  )
}
